type QueryParamValue = string | number | boolean | null | undefined;

export interface ApiResponse<T = any> {
  data: T;
  status: number;
}

interface ApiError<T = any> extends Error {
  response: ApiResponse<T>;
}

interface ParsedResponse<T = any> {
  data: T;
  hasBody: boolean;
  isJson: boolean;
  parseFailed: boolean;
}

const buildUrl = (url: string, params: Record<string, QueryParamValue>) => {
  const query = Object.keys(params)
    .filter((key) => params[key] !== undefined && params[key] !== null)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`)
    .join('&');

  return query ? `${url}?${query}` : url;
};

const isJsonResponse = (response: Response) => {
  const contentType = response.headers.get('content-type') || '';

  return contentType.includes('application/json') || contentType.includes('+json');
};

const parseResponse = async <T extends unknown = any>(response: Response): Promise<ParsedResponse<T>> => {
  const isJson = isJsonResponse(response);

  if (response.status === 204 || response.status === 205) {
    return {
      data: null as unknown as T,
      hasBody: false,
      isJson,
      parseFailed: false
    };
  }

  const body = await response.text();

  if (!body) {
    return {
      data: null as unknown as T,
      hasBody: false,
      isJson,
      parseFailed: false
    };
  }

  if (!isJson) {
    return {
      data: body as unknown as T,
      hasBody: true,
      isJson,
      parseFailed: false
    };
  }

  try {
    return {
      data: JSON.parse(body) as T,
      hasBody: true,
      isJson,
      parseFailed: false
    };
  } catch {
    return {
      data: body as unknown as T,
      hasBody: true,
      isJson,
      parseFailed: true
    };
  }
};

const createApiError = <T extends unknown = any>(message: string, response: ApiResponse<T>) => {
  const error = new Error(message) as ApiError<T>;
  error.response = response;

  return error;
};

const get = async <T extends unknown = any>(url: string, params: Record<string, QueryParamValue>): Promise<ApiResponse<T>> => {
  const response = await fetch(buildUrl(url, params), {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json'
    }
  });
  const parsed = await parseResponse<T>(response);
  const result: ApiResponse<T> = {
    data: parsed.data,
    status: response.status
  };

  if (!response.ok) {
    throw createApiError(`Request failed with status code ${response.status}`, result);
  }

  if (parsed.parseFailed) {
    throw createApiError(`Failed to parse JSON response with status code ${response.status}`, result);
  }

  if (parsed.hasBody && !parsed.isJson) {
    throw createApiError(`Expected JSON response with status code ${response.status}`, result);
  }

  return result;
};

const apiService = () => {
  const urlPrefix = '/webwidget';

  const getSettings = async (orgId:string) => {
    const url = `${urlPrefix}/settings`;
    return await get(url, {
      o: orgId
    });
  };

  const getMessages = async (orgId:string, baseTime:number, stream?: string ) => {
    const url = `${urlPrefix}/messages`;
    return await get(url, {
      o: orgId,
      t: baseTime,
      s: stream
    })
  };

  const getMessageDetail = async (orgId:string, messageId:string) => {
    const url = `${urlPrefix}/messages/${messageId}`;
    return await get(url, {
      o: orgId,
    })
  }

  return {
    getSettings,
    getMessages,
    getMessageDetail
  };

}

export default apiService()
